<script>
   /*************************************************************/
   /* PlotDistances for PCA/SIMCA results                       */
   /* ----------------------------                              */
   /* shows plot with individual score, orthogonal or full      */
   /* distances                                                 */
   /*************************************************************/

   import { Legend, Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';
   import { max } from 'mdatools/stat';
   import { objmap, csvRow, downloadCSV } from '../utils.js';

   import { pcamodel_get, pcares_get } from '@mdatools-apps/wasm/pca.js';

   import { getColorGroups, FACE_TCOLORS, LINE_COLORS, MARKERS, MAX_LABEL_SYMBOLS, MARGINS } from './Defaults.js';
   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';
   import { plotSize, legendNames } from './layout.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // to constants
   const xLabel = 'Objects';
   const legendPosition = 'topright';
   const distLabels1 = {
      'q': 'Orthogonal distance, q/q0',
      'h': 'Score distance, h/h0',
      'f': 'Full distance, f/f0',
   };
   const distLabels2 = {
      'q': 'Orthogonal distance, log(1 + q/q0)',
      'h': 'Score distance, log(1 + h/h0)',
      'f': 'Full distance, log(1 + f/f0)',
   };

   const LINE_COLOR = '#909090';    // color of the limits lines


   // get properties needed for the plot
   const classres = $derived(props && props.classres ? props.classres : null);
   const res = $derived(props && props.res ? props.res : null);
   const simca = $derived(props && props.simca ? props.simca : null);
   const model = $derived(props && props.model ? props.model : null);
   const data = $derived(props && props.data ? props.data : null);

   // get plot paramaters
   const resID0 = $derived(res ? Object.keys(res)[0] : null);
   const res0 = $derived(resID0 ? res[resID0] : null);
   const data0 = $derived(resID0 ? data[resID0] : null);

   // get distance parameters
   const ncomp = $derived(simca ? simca.ncomp : null)
   const Nf = $derived(simca ? simca.Nf : null);
   const lt = $derived(simca ? simca.limType.charAt(0) : null);
   const h0 = $derived(model ? pcamodel_get(model, lt + 'h0') : null);
   const q0 = $derived(model ? pcamodel_get(model, lt + 'q0') : null);

   // get scaled distance values for each result object
   const getdist = (r, dname, ncomp, u0) => new Vector(pcares_get(r, dname, ncomp).map(v => v / u0))
   const h = $derived(res && ncomp ? objmap(res, (obj, id) => getdist(obj[id], 'hdist', ncomp, h0[ncomp - 1])) : null);
   const q = $derived(res && ncomp ? objmap(res, (obj, id) => getdist(obj[id], 'qdist', ncomp, q0[ncomp - 1])) : null);
   const f = $derived(res && ncomp ? objmap(res, (obj, id) => getdist(obj[id], 'fdist' + lt, ncomp, Nf[ncomp - 1])) : null);
   const dist = $derived(h && q && f ? {h, q, f} : null);

   function transform(x, t) {
      if (!t || t == "no") return x;
      return new Vector(x.v.map(v => Math.log(1 + v)))
   }

   // compute values and colors for y-series
   const ys = $derived.by(() => {

      if (!dist) return null;

      if (params.resID === 'both') {
         return objmap(dist[params.distType], (obj, id) => ({
            'values': obj[id],
            'mainColor': LINE_COLORS[id],
            'faceColor': FACE_TCOLORS[id],
            'marker': MARKERS[id],
            'max': max(obj[id])
         }));
      }

      if (params.resID === 'diff') {
         const d1 = dist[params.distType]['cal'];
         const d2 = dist[params.distType]['pv'];
         const d = d1.subtract(d2).apply(v => Math.abs(v));
         return {
            'abs(cal - pv)': {
               'values': d,
               'mainColor': '#a0a0a0',
               'faceColor': '#a0a0a0',
               'max': max(d)
            }
         };
      }

      const out = {};
      const v = transform(dist[params.distType][params.resID], params.transType);
      out[params.resID] = {
         'values': v,
         'max': max(v),
         'mainColor': LINE_COLORS[params.resID],
         'faceColor': FACE_TCOLORS[params.resID],
         'marker': MARKERS[params.resID],
      };
      return out;
   });

   // compute legend
   let legendItems = $derived(ys ? Object.keys(ys).map(v => ({
      id: v,
      label: v,
      point: {
         marker: params.resID === 'diff' ? 2 : ys[v].marker,
         faceColor: ys[v].faceColor,
         lineColor: ys[v].mainColor,
         markerSize: 0.85,
         lineWidth: 1.5
      },
   })) : null);

   // compute critical limits
   const ev = $derived(simca && ncomp ? simca.eCrit[ncomp - 1] / Nf[ncomp - 1] : null);
   const ov = $derived(simca && ncomp ? simca.oCrit[ncomp - 1] / Nf[ncomp - 1] : null);

   const evt = $derived(ev ? (params.transType && params.transType === "log" ? Math.log(1 + ev) : ev) : null);
   const ovt = $derived(ov ? (params.transType && params.transType === "log" ? Math.log(1 + ov) : ov) : null);

   const ey = $derived(evt && params.distType === 'f' && params.resID !== 'diff' ? evt : 0);
   const oy = $derived(ovt && params.distType === 'f' && params.resID !== 'diff' ? ovt : 0);

   // colors and indices
   let col = $derived(getColorGroups(data0, res0, params.colorBy, params.colorByVar, params.resID, null));

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(col?.byClass ? legendNames(col.labels, size, MARGINS, true) : col?.labels);

   // compute other plot parameters
   const yMax = $derived(ys ? max(Object.keys(ys).map(id => ys[id].max).concat([oy])) : null);
   const xValues = $derived(res0 ? vector(res0.objindices).apply(v => v + 1) : null);
   const limX = $derived(xValues && xValues.v.length > 0 ? [0, max(xValues) + 1] : [0, 1]);
   const limY = $derived(yMax ? [-yMax * 0.05, yMax * 1.2] : [0, 1]);
   const yLabel = $derived(!params.transType || params.transType === "no" ? distLabels1[params.distType] : distLabels2[params.distType]);
   const labels = $derived(res0 ? res0.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);
   const title = $derived(params.title ? params.title : 'Distances (A = ' + ncomp + ')');

   // check if outliers boundary must be shown
   const isAlt = $derived.by(() => {
      if (!classres || classres.length > 1) return false;
      const cr = classres[Object.keys(res)[0]];
      if (cr.nMembers < 1) return true;
      return false;
   });

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      // 'data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(data)[0];

      let x = [];
      let y = [];
      let l = [];
      let c = [];
      let s = [];

      const xv = Array.from(xValues.v);
      const nv = xv.length;
      const classes = res0.classes && res0.classes.length > 0 ? res0.classes : Array(nv).fill("");

      for (let name of Object.keys(ys)) {
         const ysn = ys[name];
         s = [...s, ...Array(nv).fill(name)];
         x = [...x, ...xv];
         y = [...y, ...Array.from(ysn.values.v)];
         l = [...l, ...res0.objlabels];
         c = [...c, ...classes]
      }

      let out = csvRow(['Set', 'Labels', 'Groups', xLabel, yLabel], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([s[i], l[i], c[i], x[i], y[i]], sep, dec);
      }

      downloadCSV(out, 'plot-distances');
   }
</script>

{#if xValues && ys}
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {xLabel} {yLabel} {downloadCSVCallback} fileName='plot-distances'>

   {#if ey > 0}
   <Lines xValues={limX} yValues={[ey, ey]} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if oy > 0 && params.resID && params.resID !== "new" && !isAlt}
   <Lines xValues={limX} yValues={[oy, oy]} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   {#each Object.keys(ys) as id}
   <PlotSeries
      {labels}
      {selected}
      {callbacks}
      {xValues}
      yValues={ys[id].values}
      mainColor={ys[id].mainColor}
      faceColor={ys[id].faceColor}
      marker={ys[id].marker}
      colors={params.colorBy && params.colorBy !== "none" ? col.colors : null}
      plotType={params.resID === 'diff' ? 'h' : 'p'}
      showLabels={params.showLabels}
   />
   {/each}


   <!-- color by legend-->
   {#if col && col.showBar}
      <ColormapLegend show={col.breaks !== null} colmap={col.map} breaks={col.breaks} labels={legendLabels}/>
   {/if}

   {#if legendItems && legendItems.length > 0}
      <Legend items={legendItems} position={legendPosition} show={!params.colorBy || params.colorBy === "none"}/>
   {/if}
</Axes>
</div>
{/if}
